import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, ArrowRight, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import type { ResolutionProblems, ResolutionState } from '@/lib/disruptions/types';
import { vendorLabel, vendorListLabel } from './labels';
import { towerPath } from '@/routes/disruptions/operar/operate-types';

// disruption-overview (epic NEX-376 / slice U2) — "What went wrong and what do I do?".
//
// The cockpit's PROBLEMS panel: everything blocking a clean resolution, each with a
// friendly label, a recommended next action, and a DEEP-LINK to where the operator
// fixes it (the Passengers tab #passengers / the Hotels tab #allocation on this same
// disruption route). When nothing is wrong it shows a calm "all clear" state.
//
// Pure read off the B1 `state.problems` aggregate (WS-refreshed by the cockpit) —
// counts + labels only, never raw passenger data. No interval poll here.

interface ResolutionProblemsPanelProps {
  state: ResolutionState;
  disruptionUrn: string;
}

type ProblemHash = '#passengers' | '#allocation';

interface ProblemRow {
  key: string;
  label: string;
  action: string;
  hash: ProblemHash;
  tone: 'error' | 'warning';
}

/** Derive the ordered problem rows from the aggregate. Only non-empty problems
 *  produce a row; the order puts the hard blockers (no-options / gaps) first. */
function buildRows(problems: ResolutionProblems, t: TFunction<'rebooking'>): ProblemRow[] {
  const rows: ProblemRow[] = [];

  for (const [reason, count] of Object.entries(problems.noOptionsByReason ?? {})) {
    if (count > 0) {
      rows.push({
        key: `no-options-${reason}`,
        label: t('problems.noOptions.label', { count, reason }),
        action: t('problems.noOptions.action'),
        hash: '#passengers',
        tone: 'error',
      });
    }
  }

  if (problems.allocationGaps > 0) {
    rows.push({
      key: 'allocation-gaps',
      label: t('problems.gaps.label', { count: problems.allocationGaps }),
      action: t('problems.gaps.action'),
      hash: '#allocation',
      tone: 'error',
    });
  }

  if (problems.degradedGroups > 0) {
    rows.push({
      key: 'degraded',
      label: t('problems.degraded.label', { count: problems.degradedGroups }),
      action: t('problems.degraded.action'),
      hash: '#passengers',
      tone: 'warning',
    });
  }

  if (problems.delayedBy && problems.delayedBy.length > 0) {
    rows.push({
      key: 'delayed',
      label: t('problems.delayed.label', { vendors: vendorListLabel(problems.delayedBy, t) }),
      action: t('problems.delayed.action'),
      hash: '#passengers',
      tone: 'warning',
    });
  }

  if (problems.slowBy && problems.slowBy.length > 0) {
    const names = problems.slowBy
      .map((s) => `${vendorLabel(s.vendor, t)} (~${Math.round(s.avgLatencyMs)}ms)`)
      .join(', ');
    rows.push({
      key: 'slow',
      label: t('problems.slow.label', { names }),
      action: t('problems.slow.action'),
      hash: '#passengers',
      tone: 'warning',
    });
  }

  // "Waiting for the passenger" is a normal state (the acceptance step already
  // says so) — NOT a problem. A DECLINED offer is: someone has to re-plan that PNR.
  if ((problems.declined ?? 0) > 0) {
    rows.push({
      key: 'declined',
      label: t('problems.declined.label', { count: problems.declined ?? 0 }),
      action: t('problems.declined.action'),
      hash: '#passengers',
      tone: 'warning',
    });
  }

  return rows;
}

function PanelHeading() {
  const { t } = useTranslation('rebooking');
  return (
    <div className="flex items-center gap-2">
      <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden />
      <h3 className="text-sm font-semibold text-foreground">{t('problems.heading')}</h3>
    </div>
  );
}

export function ResolutionProblemsPanel({
  state,
  disruptionUrn,
}: ResolutionProblemsPanelProps) {
  const { t } = useTranslation('rebooking');
  const rows = buildRows(state.problems, t);

  // Nothing went wrong → nothing to show. An "all clear" card under a
  // resolved sequence only invites the question it answers.
  if (rows.length === 0) return null;

  // the hash jumps to a desk of «Modo experto» (the Tower)
  const base = towerPath(disruptionUrn);

  return (
    <Card data-testid="resolution-problems-panel">
      <CardContent className="flex flex-col gap-3 p-4">
        <PanelHeading />
        <ul className="flex flex-col gap-2" data-testid="resolution-problems-list">
          {rows.map((r) => (
            <li
              key={r.key}
              data-testid={`resolution-problem-${r.key}`}
              className="rounded-lg border border-border bg-muted/30 p-3"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    r.tone === 'error'
                      ? 'text-destructive'
                      : 'text-amber-600 dark:text-amber-400',
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{r.label}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{r.action}</p>
                  <Link
                    to={`${base}${r.hash}`}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid={`resolution-problem-link-${r.key}`}
                  >
                    {r.hash === '#allocation'
                      ? t('problems.goToHotels')
                      : t('problems.goToPassengers')}
                    <ArrowRight className="h-3 w-3" aria-hidden />
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
